/**
 * Cart-token resolution for revenue events (`add_to_cart`, `buy_now`).
 *
 * Boost requires `ct` (cart token) on revenue events for accurate attribution.
 * Shopify's `/cart/add.js` response doesn't always include the token, and
 * `/cart.js` is eventually-consistent — the token may need a moment to
 * propagate after an add. Strategy is a race:
 *
 *   1. `cart:updated` listener — picks up `event.detail.cart.token` the
 *      instant the theme dispatches it (the existing `shopifyThemeCart.ts`
 *      already fires this; the cart-drawer's `renderContents` does too).
 *      Fast path, usually <100ms after the add.
 *   2. `/cart.js` polling — 5 retries at 1500ms intervals. A token counts as
 *      valid when it's a non-empty string — the same truthy check Boost's own
 *      bundle uses (`o.token && …`). Fallback when no listener fires (theme
 *      doesn't dispatch `cart:updated`).
 *
 * The first strategy to find a token wins. Neither strategy settles the race
 * on failure — only the 7.5s timeout resolves `null` — so an exhausted poll
 * can't cut short a `cart:updated` that's still pending within the budget. If
 * the budget elapses with no token, the ATC event fires without `ct`.
 * Acceptable trade-off: revenue attribution suffers for that one event;
 * behavioural data still lands.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const POLL_INTERVAL_MS = 1500;
const POLL_MAX_ATTEMPTS = 5;
const TOTAL_TIMEOUT_MS = 7500;
function isValidToken(token) {
    return typeof token === 'string' && token.length > 0;
}
/**
 * Poll `/cart.js` for the token. Matches the bundle's retry pattern
 * (5x@1500ms). Resolves with the token once found; on exhaustion returns a
 * promise that never settles, so a failed poll can't win the race against a
 * still-pending `cart:updated` listener (only the overall timeout resolves
 * the no-token outcome).
 */
function pollCartJs() {
    return (() => __awaiter(this, void 0, void 0, function* () {
        for (let attempt = 1; attempt <= POLL_MAX_ATTEMPTS; attempt++) {
            try {
                const response = yield fetch('/cart.js', { headers: { Accept: 'application/json' } });
                if (response.ok) {
                    const cart = (yield response.json());
                    if (isValidToken(cart.token))
                        return cart.token;
                }
            }
            catch (_a) {
                /* ignore and retry */
            }
            if (attempt < POLL_MAX_ATTEMPTS) {
                yield new Promise(r => window.setTimeout(r, POLL_INTERVAL_MS));
            }
        }
        return new Promise(() => { }); // exhausted: never settle
    }))();
}
/**
 * Resolve the current cart token. Returns `null` on timeout — the caller is
 * expected to fire the event without `ct` rather than block indefinitely.
 *
 * @param timeoutMs Total budget across both strategies. Defaults to 7.5s
 *   (enough for the polling fallback to exhaust 5 retries with margin).
 */
export function resolveCartToken() {
    return __awaiter(this, arguments, void 0, function* (timeoutMs = TOTAL_TIMEOUT_MS) {
        let removeListener = () => { };
        // `cart:updated` listener — resolves only when an event carries a valid
        // token; never settles otherwise (cleanup is handled below once the race
        // resolves), so it can't lose the race to an exhausted poll.
        const fromEvent = new Promise(resolve => {
            const handler = (event) => {
                var _a, _b;
                const token = (_b = (_a = event.detail) === null || _a === void 0 ? void 0 : _a.cart) === null || _b === void 0 ? void 0 : _b.token;
                if (isValidToken(token))
                    resolve(token);
            };
            document.addEventListener('cart:updated', handler);
            removeListener = () => document.removeEventListener('cart:updated', handler);
        });
        const overall = new Promise(resolve => {
            window.setTimeout(() => resolve(null), timeoutMs);
        });
        const token = yield Promise.race([fromEvent, pollCartJs(), overall]);
        removeListener();
        return token;
    });
}
